import * as React from "react";
import { twMerge } from "tailwind-merge";
import { Presence, SlideIn } from "@/primitives/motion";
import { BackButton } from "../components/BackButton";
import { StickyRootNode } from "../components/StickyRootNode";
import type { HierarchyNode, StickyNodeState } from "../types";

interface SingleLayoutProps<T extends HierarchyNode> {
  currentItems: T[];
  currentParent: T | null;
  navigationStack: T[];
  selectedId: string | null;
  variant: "subtle" | "solid";
  size: "xs" | "sm" | "md";
  showBullets: boolean;
  slideDirection: "left" | "right";
  viewKey: string;
  hasHeight: boolean;
  rootNodeStickyState: StickyNodeState<T>;
  renderNodeList: (items: T[], showConnector: boolean) => React.ReactNode;
  renderDetailContent: (parent: T | null) => React.ReactNode;
  onNavigateBack: () => void;
}

export function SingleLayout<T extends HierarchyNode>({
  currentItems,
  currentParent,
  navigationStack,
  selectedId,
  variant,
  size,
  showBullets,
  slideDirection,
  viewKey,
  hasHeight,
  rootNodeStickyState,
  renderNodeList,
  renderDetailContent,
  onNavigateBack,
}: SingleLayoutProps<T>) {
  return (
    <>
      {/* Floating Sticky Root Node (rendered when out of view, only on root view) */}
      {navigationStack.length === 0 && rootNodeStickyState.position && rootNodeStickyState.node && (
        <StickyRootNode
          node={rootNodeStickyState.node}
          position={rootNodeStickyState.position}
          selectedId={selectedId}
          variant={variant}
          size={size}
          showBullets={showBullets}
        />
      )}

      {/* Back Button (when drilled down) */}
      {navigationStack.length > 0 && (
        <BackButton navigationStack={navigationStack} onNavigateBack={onNavigateBack} size={size} />
      )}

      {/* Main Content with Slide Animation */}
      <div className={twMerge("relative", hasHeight && "overflow-auto h-full")}>
        <Presence mode="wait" presenceKey={viewKey}>
          {navigationStack.length === 0 ? (
            // Root view - no animation
            renderNodeList(currentItems, true)
          ) : (
            // Drilled-down view - with slide animation
            <SlideIn direction={slideDirection} distance="100%" duration="normal">
              {renderDetailContent(currentParent)}
            </SlideIn>
          )}
        </Presence>
      </div>
    </>
  );
}
